/**
 * Performance (WALLIYO-0131), as the mockup draws it: a cycle, the reviews
 * waiting on you, your own, and — for HR — everybody's with the odd cases
 * flagged.
 *
 * A review with no reviewer says which of three reasons it is rather than
 * showing a blank, because a blank is a thing nobody chases and each of the
 * three is fixed differently: put them on a team, give the team a lead, or
 * decide who reviews the lead.
 */

import type { PerformanceReview, ReviewCycle, ReviewerGap } from "@wallyo/contracts/console/hr";
import { t, type MessageId } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { hrHref } from "./hr-route";

export type PerformanceState =
  | { status: "loading"; data: PerformanceData | null }
  | { status: "ready"; data: PerformanceData }
  | { status: "failed"; data: PerformanceData | null; error: unknown };

export type PerformanceData = {
  cycle: ReviewCycle | null;
  cycles: readonly ReviewCycle[];
  toWrite: readonly PerformanceReview[];
  mine: readonly PerformanceReview[];
  everybody: readonly PerformanceReview[];
};

const STATUS_LABEL: Record<PerformanceReview["status"], MessageId> = {
  not_started: "performance.status.notStarted",
  in_progress: "performance.status.inProgress",
  submitted: "performance.status.submitted",
  signed_off: "performance.status.signedOff",
};

const STATUS_TONE: Record<PerformanceReview["status"], string> = {
  not_started: "pill",
  in_progress: "pill warn",
  submitted: "pill ok",
  signed_off: "pill ok",
};

const GAP_LABEL: Record<ReviewerGap, MessageId> = {
  no_team: "performance.gap.noTeam",
  no_lead: "performance.gap.noLead",
  leads_own_team: "performance.gap.leadsOwn",
};

/** Who is writing it, or which kind of nobody. */
function Reviewer({ review }: { review: PerformanceReview }) {
  if (review.reviewerName) return <>{review.reviewerName}</>;
  return <span className="pill bad">{review.gap ? t(GAP_LABEL[review.gap]) : t("performance.gap.noLead")}</span>;
}

function Rows({
  reviews,
  onWrite,
}: {
  reviews: readonly PerformanceReview[];
  onWrite?: (review: PerformanceReview) => void;
}) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t("performance.col.who")}</th>
            <th>{t("performance.col.reviewer")}</th>
            <th>{t("performance.col.status")}</th>
            <th>{t("performance.col.outcome")}</th>
            {onWrite ? <th /> : null}
          </tr>
        </thead>
        <tbody>
          {reviews.map((review) => (
            <tr key={review.performanceReviewId}>
              <td>
                <a href={hrHref({ uuid: review.uuid })}>{review.name}</a>
              </td>
              <td>
                <Reviewer review={review} />
              </td>
              <td>
                <span className={STATUS_TONE[review.status]}>{t(STATUS_LABEL[review.status])}</span>
                {review.submittedAt ? <div className="foot">{formatDateTime(review.submittedAt)}</div> : null}
              </td>
              <td>
                {review.outcome ?? <span className="foot">—</span>}
                {review.note ? <div className="foot">{review.note}</div> : null}
              </td>
              {onWrite ? (
                <td className="num">
                  <button className="btn quiet small" type="button" onClick={() => onWrite(review)}>
                    {review.status === "not_started" ? t("performance.write") : t("performance.keepWriting")}
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PerformanceView(props: {
  state: PerformanceState;
  onCycle: (reviewCycleId: string) => void;
  onOpen: () => void;
  onWrite: (review: PerformanceReview) => void;
  onRetry: () => void;
}) {
  const { state } = props;
  const data = state.data;
  // A cycle where a third of the reviews have nobody to write them is a fact
  // about the org, not about the cycle, so it is said once at the top.
  const withoutReviewer = (data?.everybody ?? []).filter((each) => each.reviewerId === null).length;

  return (
    <>
      <div className="hr-search">
        <label className="field">
          <span className="label">{t("performance.cycle")}</span>
          <select
            value={data?.cycle?.reviewCycleId ?? ""}
            onChange={(event) => props.onCycle(event.currentTarget.value)}
          >
            {(data?.cycles ?? []).map((cycle) => (
              <option key={cycle.reviewCycleId} value={cycle.reviewCycleId}>
                {cycle.name}
              </option>
            ))}
          </select>
        </label>
        <div className="grow" />
        <button className="btn" type="button" onClick={props.onOpen} disabled={state.status !== "ready"}>
          {t("performance.openCycle")}
        </button>
      </div>

      {state.status === "failed" ? (
        <div className="audit-error">
          <p className="foot">{t("hr.failed")}</p>
          <ErrorNotice error={state.error} />
          <div className="btns">
            <button className="btn" type="button" onClick={props.onRetry}>
              {t("hr.retry")}
            </button>
          </div>
        </div>
      ) : null}

      {!data && state.status === "loading" ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("hr.loading")}</span>
        </div>
      ) : null}

      {data && data.cycle === null ? <div className="empty">{t("performance.noCycle")}</div> : null}

      {data && data.cycle ? (
        <>
          <p className="foot">{t("performance.runs", { from: data.cycle.opensOn, to: data.cycle.closesOn })}</p>

          {/* What somebody else is waiting on, before anything of your own. */}
          {data.toWrite.length > 0 ? (
            <section className="card">
              <h2>{t("performance.toWrite.title")}</h2>
              <p className="foot">{t("performance.toWrite.help")}</p>
              <Rows reviews={data.toWrite} onWrite={props.onWrite} />
            </section>
          ) : null}

          <section className="card">
            <h2>{t("performance.mine.title")}</h2>
            {data.mine.length === 0 ? (
              <p className="foot">{t("performance.mine.none")}</p>
            ) : (
              <Rows reviews={data.mine} />
            )}
          </section>

          {data.everybody.length > 0 ? (
            <section className="card">
              <h2>{t("performance.everybody.title")}</h2>
              <p className="foot">
                {withoutReviewer > 0
                  ? t("performance.everybody.gaps", { count: String(withoutReviewer) })
                  : t("performance.everybody.help")}
              </p>
              <Rows reviews={data.everybody} />
            </section>
          ) : null}
        </>
      ) : null}
    </>
  );
}
